import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import {
    subsetGetter,
    SubsetGetterProps,
    Subsets,
    subsetSetter,
    subsetUpdateSetter,
} from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import { getConfig, klinikenApi } from "@/api";
import cloneDeep from "lodash/cloneDeep";
import { Besoksmall } from "@/tabs/Oppenvard/types";

type State = BaseState & {
    besoksmallar: Map<Besoksmall["id"], Besoksmall>;
};

export const convertBesoksmallData: (args: { besoksmall?: Besoksmall }) => { value: number; label: string } | null = ({
    besoksmall,
}) => {
    if (!besoksmall) {
        return null;
    }

    return {
        value: besoksmall.id,
        label: besoksmall.namn,
    };
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        besoksmallar: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "besoksmallar";
const baseUrl = "oppenvard/besoksmallar/";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        besoksmall: (state: State) => [...state.besoksmallar.values()],
        besoksmallSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                limit: 0,
                ...props, // Skriver över limit om den finns i props
                state,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) => convertBesoksmallData({ besoksmall: singleResult }));
            }

            return result;
        },
        besoksmallById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.besoksmallar.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addBesoksmallar: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                besoksmallar = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                besoksmallar: Besoksmall[];
            }
        ) =>
            subsetSetter<State, Besoksmall>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: besoksmallar,
            }),
        updateBesoksmall: (
            state: State,
            {
                besoksmallar,
                offset,
                subsetName,
            }: {
                besoksmallar: Besoksmall[];
                offset: number;
                subsetName: keyof Subsets;
            } // TODO subsetName kommer skickas in i framtiden
        ) =>
            subsetUpdateSetter<State, Besoksmall>({
                offset,
                state,
                collectionName: moduleName,
                items: besoksmallar,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        fetchBesoksmallar: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
                limit = 20,
                page = 1,
            }: {
                limit: number;
                page: number;
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const { subsetName, results, total, nextPage, offset } = await fetchItems<State, Besoksmall>({
                    filterParams,
                    state,
                    config,
                    page,
                    limit,
                    collectionName: moduleName,
                    apiUrl: baseUrl,
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addBesoksmallar", {
                    subsetName,
                    offset,
                    besoksmallar: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) => convertBesoksmallData({ besoksmall: singleResult }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        createBesoksmall: async (
            { commit, state }: BaseActionsProps<State>,
            {
                besoksmall,
            }: {
                besoksmall: Besoksmall;
            }
        ) => {
            try {
                const response = await klinikenApi.post(baseUrl, besoksmall, getConfig({}));

                // TODO: Hur får jag den att läggas till i alla subsets som den ska ingå i?
                commit("addBesoksmallar", {
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    subsetName: "newlyCreated",
                    besoksmallar: [response.data],
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        updateBesoksmall: async (
            { commit, state }: BaseActionsProps<State>,
            {
                besoksmall,
            }: {
                besoksmall: Besoksmall;
            }
        ) => {
            try {
                const response = await klinikenApi.put(`${baseUrl}${besoksmall.id}/`, besoksmall, getConfig({}));

                commit("updateBesoksmall", {
                    subsetName: "newlyUpdated",
                    besoksmallar: [response.data],
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
